/**
 * ScopeBulletsModal — in-place "I have this" conversation modal.
 *
 * Opens over the page when a user clicks "I have this — let's talk about it"
 * on a requirement row. Wraps the same conversation + bullets pipeline used
 * by the full /enhancement page, but in a lighter modal-shaped UI:
 *
 *   ┌─ Scope your evidence ─────────────────────────[X]─┐
 *   │ "Build positioning and messaging frameworks…"     │
 *   │ [target ●●●●○ · you ●●●○○]   [skill]              │
 *   │ Your evidence so far:  • A · B · C                │
 *   ├───────────────────────────────────────────────────┤
 *   │ Conversation                                      │
 *   │ [messages…]                                       │
 *   │ [Type your response…] [→]                         │
 *   ├───────────────────────────────────────────────────┤
 *   │ Bullets we've drafted (N)                         │
 *   │ [bullet cards stack as the chat progresses]       │
 *   └───────────────────────────────────────────────────┘
 *
 * The modal owns its own conversation state — opens fresh each time.
 * Calls /api/profile/enhancement/conversations-sync once on open, then
 * /messages for each user reply. No navigation; closing returns to the page.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import { SAGE, CREAM } from '../../theme';
import { API_URL } from '../../config';
import { mockAuthHeaders } from '../../utils/mockAuth';
import { TargetDots } from '../jobs/RequirementRow';
import { useIsDemoUser, findFixture } from '../../utils/demoMode';
import { SCOPE_FIXTURES, DEMO_USER_REPLY_ACK } from '../../data/demo_conversations';

const F = "'Outfit', sans-serif";
const SERIF = "'Lora', Georgia, serif";

const buildTopic = (text) => `Your experience with: ${text}`;

const ScopeBulletsModal = ({ open, onClose, requirement, roleContext }) => {
  const isDemoUser = useIsDemoUser();
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [bullets, setBullets] = useState([]);
  const [input, setInput] = useState('');
  const [starting, setStarting] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [usedFixture, setUsedFixture] = useState(false);
  const messagesEndRef = useRef(null);

  // Fresh conversation each time the modal opens with a new requirement.
  // Demo users (e.g. Margot) get an instant pre-baked fixture when one
  // matches the requirement text — avoids the ~20s /conversations-sync
  // cold-start during live demos. Falls through to real backend otherwise.
  useEffect(() => {
    if (!open || !requirement) return;
    let cancelled = false;
    setMessages([]);
    setBullets([]);
    setConversationId(null);
    setInput('');
    setError(null);
    setUsedFixture(false);

    if (isDemoUser) {
      const fixture = findFixture(SCOPE_FIXTURES, requirement.text);
      if (fixture) {
        setMessages(fixture.messages || []);
        setBullets(fixture.bullets || []);
        setUsedFixture(true);
        setStarting(false);
        return () => { cancelled = true; };
      }
    }

    setStarting(true);
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/api/profile/enhancement/conversations-sync`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...mockAuthHeaders() },
          credentials: 'include',
          body: JSON.stringify({
            topic: buildTopic(requirement.text),
            context: {
              source: 'requirement_row',
              requirement_text: requirement.text,
              role: roleContext?.role,
              level: roleContext?.level,
            },
          }),
        });
        if (!resp.ok) throw new Error(`${resp.status}`);
        const data = await resp.json();
        if (cancelled) return;
        setConversationId(data.conversation_id || null);
        setMessages(data.messages || []);
        setBullets(data.bullets || []);
      } catch (err) {
        if (!cancelled) setError('Could not start the conversation. Try again in a moment.');
      } finally {
        if (!cancelled) setStarting(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, requirement, roleContext, isDemoUser]);

  // Auto-scroll the chat region as messages stream in.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const optimistic = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };

    // Demo-fixture path: append the user message + a canned ack instead
    // of hitting the backend. Keeps the chat reactive without the
    // /messages roundtrip that the demo was trying to skip.
    if (usedFixture && !conversationId) {
      setMessages((prev) => [...prev, optimistic, DEMO_USER_REPLY_ACK(text)]);
      setInput('');
      return;
    }

    if (!conversationId) return;
    setSending(true);
    setError(null);
    setMessages((prev) => [...prev, optimistic]);
    setInput('');
    try {
      const resp = await fetch(
        `${API_URL}/api/profile/enhancement/conversations/${conversationId}/messages`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...mockAuthHeaders() },
          credentials: 'include',
          body: JSON.stringify({ content: text }),
        }
      );
      if (!resp.ok) throw new Error(`${resp.status}`);
      const data = await resp.json();
      setMessages(data.messages || []);
      setBullets(data.bullets || []);
    } catch (err) {
      setError('Could not send. Try again.');
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!requirement) return null;
  const evidence = requirement.evidence || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          bgcolor: CREAM[50],
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3, pt: 2.5, pb: 2,
          borderBottom: `1px solid ${SAGE[200]}`,
          display: 'flex', alignItems: 'flex-start', gap: 2,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: F, fontSize: '0.65rem', fontWeight: 700,
              letterSpacing: 1.6, textTransform: 'uppercase', color: SAGE[500], mb: 0.5,
            }}
          >
            Scope your evidence
            {roleContext?.role ? ` · ${roleContext.role}` : ''}
            {roleContext?.level ? ` · ${roleContext.level}` : ''}
          </Typography>
          <Typography
            sx={{
              fontFamily: SERIF, fontSize: '1.15rem', fontWeight: 500,
              color: SAGE[800], lineHeight: 1.35,
            }}
          >
            {requirement.text}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: SAGE[500], mt: 0.25 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Requirement context strip */}
      <Box
        sx={{
          px: 3, py: 2,
          borderBottom: `1px solid ${SAGE[100]}`,
          display: 'flex', flexDirection: 'column', gap: 1.5,
          bgcolor: `${SAGE[100]}55`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {requirement.target != null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontFamily: F, fontSize: '0.7rem', color: SAGE[500], fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                Fit
              </Typography>
              <TargetDots required={requirement.target} has={requirement.has || 0} />
              <Typography sx={{ fontFamily: F, fontSize: '0.72rem', color: SAGE[500] }}>
                {(requirement.has || 0) >= requirement.target
                  ? `you meet it (${requirement.has || 0}/${requirement.target})`
                  : `role asks ${requirement.target}, you have ${requirement.has || 0}`}
              </Typography>
            </Box>
          )}
          {requirement.category && (
            <Box
              sx={{
                fontFamily: F, fontSize: '0.62rem', fontWeight: 700,
                letterSpacing: 0.4, textTransform: 'uppercase',
                px: 1, py: 0.25, borderRadius: 1,
                bgcolor: SAGE[200], color: SAGE[700],
              }}
            >
              {requirement.category}
            </Box>
          )}
        </Box>
        {evidence.length > 0 && (
          <Box>
            <Typography sx={{ fontFamily: F, fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: SAGE[500], mb: 0.5 }}>
              Evidence already in your profile
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {evidence.slice(0, 3).map((ev, i) => (
                <Typography
                  key={i}
                  sx={{
                    fontFamily: F, fontSize: '0.78rem', color: SAGE[700], lineHeight: 1.5,
                  }}
                >
                  · <span style={{ color: SAGE[500], fontStyle: 'italic' }}>{ev.source}</span> — {ev.text}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
        {requirement.wordings && requirement.wordings.length > 0 && (
          <Typography sx={{ fontFamily: F, fontSize: '0.72rem', color: SAGE[500], fontStyle: 'italic' }}>
            How companies word this: "{requirement.wordings[0]}"
          </Typography>
        )}
      </Box>

      {error && (
        <Box sx={{ mx: 3, mt: 2, px: 2, py: 1, bgcolor: '#f0e0e0', color: '#8a3a3a', borderRadius: 1.5, fontFamily: F, fontSize: '0.78rem' }}>
          {error}
        </Box>
      )}

      {/* Conversation */}
      <Box
        sx={{
          flex: 1, minHeight: 280, maxHeight: 380,
          display: 'flex', flexDirection: 'column',
          borderBottom: `1px solid ${SAGE[100]}`,
        }}
      >
        <Box sx={{ px: 3, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ChatBubbleOutlineIcon sx={{ fontSize: 16, color: SAGE[500] }} />
          <Typography sx={{ fontFamily: F, fontSize: '0.7rem', fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: SAGE[500] }}>
            Conversation
          </Typography>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {starting && messages.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 2 }}>
              <CircularProgress size={14} sx={{ color: SAGE[500] }} />
              <Typography sx={{ fontFamily: F, fontSize: '0.78rem', color: SAGE[500], fontStyle: 'italic' }}>
                Starting the conversation…
              </Typography>
            </Box>
          ) : (
            messages.map((m) => <MessageBubble key={m.id} message={m} />)
          )}
          {sending && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, alignSelf: 'flex-start', py: 0.5 }}>
              <CircularProgress size={12} sx={{ color: SAGE[500] }} />
              <Typography sx={{ fontFamily: F, fontSize: '0.74rem', color: SAGE[500], fontStyle: 'italic' }}>
                Thinking…
              </Typography>
            </Box>
          )}
          <div ref={messagesEndRef} />
        </Box>
        <Box sx={{ px: 3, pb: 2, pt: 0.5 }}>
          <Box
            sx={{
              display: 'flex', alignItems: 'flex-end', gap: 1,
              bgcolor: '#fff', border: `1.5px solid ${SAGE[200]}`, borderRadius: 1.5,
              p: 0.5, transition: 'border-color 0.18s',
              '&:focus-within': { borderColor: SAGE[500] },
            }}
          >
            <TextField
              fullWidth multiline maxRows={4}
              placeholder="Tell the story — what did you do, what changed?"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending || starting || !conversationId}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: { px: 1.5, py: 0.75, fontSize: '0.88rem', fontFamily: F, color: SAGE[800] },
              }}
            />
            <IconButton
              onClick={handleSend}
              disabled={!input.trim() || sending || starting || !conversationId}
              sx={{
                bgcolor: SAGE[700], color: CREAM[50], borderRadius: 1, px: 1.5, py: 1,
                '&:hover': { bgcolor: SAGE[800] },
                '&.Mui-disabled': { bgcolor: SAGE[200], color: CREAM[100] },
              }}
            >
              <SendIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Bullets */}
      <Box sx={{ px: 3, py: 2.5, bgcolor: `${CREAM[100]}80`, maxHeight: 280, overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <AutoAwesomeIcon sx={{ fontSize: 16, color: SAGE[500] }} />
          <Typography sx={{ fontFamily: F, fontSize: '0.7rem', fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: SAGE[500] }}>
            Bullets we've drafted ({bullets.length})
          </Typography>
        </Box>
        {bullets.length === 0 ? (
          <Typography sx={{ fontFamily: F, fontSize: '0.78rem', color: SAGE[500], fontStyle: 'italic' }}>
            Bullets will appear here as the conversation surfaces specifics — what changed, what you measured, who saw the result.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {bullets.map((b) => <LightBulletCard key={b.id} bullet={b} />)}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

const MessageBubble = ({ message }) => {
  const isUser = message.role === 'user';
  return (
    <Box
      sx={{
        maxWidth: '88%',
        alignSelf: isUser ? 'flex-end' : 'flex-start',
      }}
    >
      <Box
        sx={{
          px: 1.75, py: 1.25, borderRadius: 2,
          bgcolor: isUser ? SAGE[700] : '#fff',
          color: isUser ? CREAM[50] : SAGE[800],
          border: isUser ? 'none' : `1px solid ${SAGE[200]}`,
          fontFamily: F, fontSize: '0.85rem', lineHeight: 1.5,
          whiteSpace: 'pre-line',
          borderBottomRightRadius: isUser ? 4 : 16,
          borderBottomLeftRadius: isUser ? 16 : 4,
        }}
      >
        {message.content}
      </Box>
    </Box>
  );
};

const LightBulletCard = ({ bullet }) => {
  const total = bullet.scores
    ? Object.values(bullet.scores).reduce((a, b) => a + b, 0)
    : null;
  const tone = total == null
    ? { label: 'draft', color: SAGE[500] }
    : total >= 13 ? { label: 'strong', color: '#3d7a4a' }
    : total >= 10 ? { label: 'good',   color: SAGE[600] }
    : total >= 7  ? { label: 'fair',   color: '#c4956a' }
                  : { label: 'rough',  color: '#c48a8a' };
  return (
    <Box
      sx={{
        bgcolor: '#fff', borderRadius: 2, border: `1px solid ${SAGE[200]}`,
        px: 1.75, py: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {bullet.type && (
          <Box
            sx={{
              fontFamily: F, fontSize: '0.6rem', fontWeight: 700, letterSpacing: 0.5,
              textTransform: 'uppercase', px: 0.85, py: 0.15, borderRadius: 0.75,
              bgcolor: SAGE[100], color: SAGE[600],
            }}
          >
            {bullet.type}
          </Box>
        )}
        <Box
          sx={{
            fontFamily: F, fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.5,
            textTransform: 'uppercase', px: 0.85, py: 0.15, borderRadius: 0.75,
            bgcolor: `${tone.color}18`, color: tone.color,
          }}
        >
          {tone.label}
        </Box>
      </Box>
      <Typography sx={{ fontFamily: F, fontSize: '0.85rem', color: SAGE[800], lineHeight: 1.5 }}>
        {bullet.text}
      </Typography>
    </Box>
  );
};

export default ScopeBulletsModal;
